import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Header,
  Icon,
  Unlock,
  Editor,
  View,
  VisitorEmpty,
} from './LifeTapDesign';
import LifeObjectRenderer from '../experiences/LifeObjectRenderer';
import { fromLifeObject, toLifeObject } from './lifeTapContent';
import { lifeTapCall, uploadLifeTapPhoto } from './lifeTapApi';
import './lifetap.css';

function Recovery({
  identifier,
  restoring,
  emailAvailable,
  onClose,
  onRestored,
}) {
  const [choice, setChoice] = useState('menu');
  const [email, setEmail] = useState('');
  const [input, setInput] = useState('');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [restored, setRestored] = useState(false);
  async function action(fn) {
    setError('');
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      setError(e.message || 'Please try again.');
    } finally {
      setBusy(false);
    }
  }
  const close = () =>
    restored || choice === 'done' || code ? action(onRestored) : onClose();
  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="recovery-title"
    >
      <div className="modal recovery-modal">
        <button aria-label="Close" className="modal-close" onClick={close}>
          <Icon name="close" />
        </button>
        <div className="modal-icon">
          <Icon name="shield" />
        </div>
        <div className="eyebrow">
          {restoring ? 'New phone or browser' : 'Optional'}
        </div>
        <h2 id="recovery-title">
          {choice === 'done'
            ? 'You’re all set'
            : restoring
            ? 'Restore editing access'
            : 'Keep your editing access'}
        </h2>
        <p>
          If you change phones or clear this browser, a recovery option helps
          you get back in. Visitors never see it.
        </p>
        {choice === 'menu' && (
          <div className="recovery-options">
            {emailAvailable && (
              <button onClick={() => setChoice('email')}>
                <span>
                  <Icon name="mail" />
                </span>
                <div>
                  <strong>
                    {restoring ? 'Use recovery email' : 'Add a recovery email'}
                  </strong>
                  <small>We’ll send a one-time verification code</small>
                </div>
                <Icon name="chevron" />
              </button>
            )}
            <button
              disabled={busy}
              onClick={() =>
                restoring
                  ? setChoice('restore-code')
                  : action(async () => {
                      const result = await lifeTapCall(
                        'createLifeTapRecovery',
                        identifier,
                        {},
                        true
                      );
                      setCode(result.code);
                      setChoice('code');
                    })
              }
            >
              <span>
                <Icon name="lock" />
              </span>
              <div>
                <strong>
                  {restoring
                    ? 'Enter recovery code'
                    : 'Save a new recovery code'}
                </strong>
                <small>
                  {restoring
                    ? 'Use your saved code'
                    : 'Replaces any previous recovery code'}
                </small>
              </div>
              <Icon name="chevron" />
            </button>
          </div>
        )}
        {choice === 'email' && (
          <form
            className="recovery-form"
            onSubmit={(e) => {
              e.preventDefault();
              action(async () => {
                const result = await lifeTapCall(
                  'requestLifeTapEmail',
                  identifier,
                  { email, purpose: restoring ? 'restore' : 'enroll' },
                  true
                );
                setChallenge(result.challengeId);
                setInput('');
                setChoice('verify');
              });
            }}
          >
            <label htmlFor="recovery-email">Email address</label>
            <input
              autoFocus
              required
              type="email"
              id="recovery-email"
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Button type="submit" disabled={busy}>
              Send verification
            </Button>
          </form>
        )}
        {(choice === 'verify' || choice === 'restore-code') && (
          <form
            className="recovery-form"
            onSubmit={(e) => {
              e.preventDefault();
              action(async () => {
                if (choice === 'verify') {
                  await lifeTapCall(
                    'verifyLifeTapEmail',
                    identifier,
                    { challengeId: challenge, code: input },
                    true
                  );
                  setRestored(restoring);
                  setChoice('done');
                } else {
                  const result = await lifeTapCall(
                    'restoreLifeTap',
                    identifier,
                    { code: input },
                    true
                  );
                  setRestored(true);
                  setCode(result.code);
                  setChoice('code');
                }
              });
            }}
          >
            <label htmlFor="recovery-code">
              {choice === 'verify' ? 'Code from your email' : 'Recovery code'}
            </label>
            {choice === 'verify' && (
              <p className="field-help">
                If that email matches your recovery option, a code is on its
                way. It expires in 10 minutes.
              </p>
            )}
            <input
              required
              autoFocus
              autoComplete="one-time-code"
              id="recovery-code"
              className="input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />
            <Button type="submit" disabled={busy}>
              Continue securely
            </Button>
          </form>
        )}
        {choice === 'code' && (
          <div className="recovery-code">
            <small>
              {restored
                ? 'Your replacement recovery code'
                : 'Your recovery code'}
            </small>
            <strong>{code}</strong>
            <p>
              Save this in your password manager. It will not be shown again.
            </p>
            <Button onClick={() => setChoice('done')}>I saved my code</Button>
          </div>
        )}
        {choice === 'done' && (
          <Button onClick={close}>Back to this LifeTap</Button>
        )}
        {error && (
          <p className="error-message" role="alert">
            {error}
          </p>
        )}
        {choice === 'menu' && (
          <button className="skip-button" onClick={onClose}>
            {restoring ? 'Back' : 'Not now'}
          </button>
        )}
      </div>
    </div>
  );
}

export default function LifeTapExperience({ match }) {
  const identifier = match.params.identifier || match.params.id;
  const [tap, setTap] = useState(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [creationPaused, setCreationPaused] = useState(false);
  const [active, setActive] = useState(0);
  const [recovery, setRecovery] = useState(null);
  const requestId = useRef(window.crypto.randomUUID());
  const refresh = useCallback(async () => {
    const result = await lifeTapCall('getLifeTap', identifier);
    setTap(result);
    setError('');
    return result;
  }, [identifier]);
  useEffect(() => {
    let alive = true;
    setTap(null);
    setEditing(false);
    setCreationPaused(false);
    setActive(0);
    setError('');
    document.title = 'LifeTagger — LifeTap';
    lifeTapCall('getLifeTap', identifier)
      .then((result) => {
        if (alive) setTap(result);
      })
      .catch((e) => {
        if (alive) setError(e.message);
      });
    return () => {
      alive = false;
    };
  }, [identifier]);
  const object = tap?.objects?.[active];
  const draft = fromLifeObject(object);
  const create = tap?.canEdit && !object && !creationPaused;
  async function save(data) {
    const result = await lifeTapCall(
      'saveLifeTapObject',
      identifier,
      {
        ...toLifeObject(data, object),
        objectId: object?.objectId,
        updatedAt: object?.updatedAt,
        requestId: requestId.current,
      },
      true
    );
    // Use the saved response immediately, without a second request blocking viewing.
    setTap((current) => ({
      ...current,
      objects: object
        ? current.objects.map((row) =>
            row.objectId === result.objectId ? result : row
          )
        : [...current.objects, result],
    }));
    setEditing(false);
    requestId.current = window.crypto.randomUUID();
    if (!tap.recoveryConfigured) setRecovery('setup');
  }
  return (
    <div className="lifetap-experience">
      <div className="app-shell">
        {!tap && (
          <>
            <Header />
            <main className="state-page">
              <h1>
                {error ? 'Couldn’t open this LifeTap' : 'Opening your LifeTap…'}
              </h1>
              {error ? (
                <>
                  <p role="alert">{error}</p>
                  <Button
                    onClick={() => refresh().catch((e) => setError(e.message))}
                  >
                    Try again
                  </Button>
                </>
              ) : (
                <span className="spinner" role="status" aria-label="Loading" />
              )}
            </main>
          </>
        )}
        {tap && !tap.claimed && (
          <Unlock
            onRecovery={() => setRecovery('restore')}
            onSuccess={async (code) => {
              await lifeTapCall('claimLifeTapPack', identifier, { code }, true);
              await refresh();
            }}
          />
        )}
        {tap?.claimed && (editing || create) && (
          <Editor
            key={object?.objectId || 'new'}
            mode={object ? 'edit' : 'create'}
            tapName={tap.name}
            initialTitle={draft.title}
            initialNote={draft.note}
            initialPhotos={draft.photos}
            initialItems={draft.items}
            onUpload={(file) => uploadLifeTapPhoto(identifier, file)}
            onCancel={() => {
              setEditing(false);
              if (create) setCreationPaused(true);
            }}
            onSave={save}
          />
        )}
        {tap?.claimed && !editing && !create && object && (
          <View
            key={object.objectId}
            owner={tap.canEdit}
            tapName={tap.name}
            updatedAt={object.updatedAt}
            {...draft}
            onEdit={() => setEditing(true)}
            onRecovery={() => setRecovery('setup')}
          >
            {draft.remaining.length > 0 && (
              <LifeObjectRenderer
                lifeObject={{
                  ...object,
                  id: object.objectId,
                  content: draft.remaining,
                }}
              />
            )}
            {tap.objects.length > 1 && (
              <nav className="photo-actions" aria-label="LifeObjects">
                <Button
                  variant="secondary"
                  disabled={active === 0}
                  onClick={() => setActive(active - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  disabled={active === tap.objects.length - 1}
                  onClick={() => setActive(active + 1)}
                >
                  Next
                </Button>
              </nav>
            )}
          </View>
        )}
        {tap?.claimed && !editing && !create && !object && (
          <VisitorEmpty
            tapName={tap.name}
            owner={tap.canEdit}
            onCreate={() => setCreationPaused(false)}
          />
        )}
        {tap?.claimed && !tap.canEdit && (
          <div className="narrow">
            <button
              className="text-button"
              onClick={() => setRecovery('restore')}
            >
              Is this your LifeTap? Restore editing access
            </button>
          </div>
        )}
        {recovery && (
          <Recovery
            identifier={identifier}
            restoring={recovery === 'restore'}
            emailAvailable={tap?.emailRecoveryAvailable}
            onClose={() => setRecovery(null)}
            onRestored={async () => {
              await refresh();
              setCreationPaused(false);
              setRecovery(null);
            }}
          />
        )}
      </div>
    </div>
  );
}
